import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CaretRightOutlined, LeftOutlined, MenuFoldOutlined } from '@ant-design/icons';
import type { SideNavItem } from './types';
import styles from './SideNav.module.css';

function itemOrChildActive(item: SideNavItem, activeKey: string | null): boolean {
  if (!activeKey) return false;
  if (item.key === activeKey) return true;
  return (item.children ?? []).some((child) => child.key === activeKey);
}

function NavRow({
  item,
  depth,
  activeKey,
  collapsed,
  hasChildren,
  flyoutOpen,
  treeExpanded,
  onNavigate,
  onToggleFlyout,
  onToggleTree,
  buttonRef,
  flyoutId,
}: {
  item: SideNavItem;
  depth: number;
  activeKey: string | null;
  collapsed: boolean;
  hasChildren?: boolean;
  flyoutOpen?: boolean;
  treeExpanded?: boolean;
  onNavigate?: (key: string) => void;
  onToggleFlyout?: () => void;
  onToggleTree?: () => void;
  buttonRef?: React.Ref<HTMLButtonElement>;
  flyoutId?: string;
}) {
  const isActive = activeKey === item.key;
  const isGroupActive = hasChildren && itemOrChildActive(item, activeKey);

  const handleClick = () => {
    if (collapsed && hasChildren) {
      onToggleFlyout?.();
      return;
    }
    if (!collapsed && hasChildren) {
      onToggleTree?.();
      return;
    }
    onNavigate?.(item.key);
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      title={collapsed ? item.label : undefined}
      aria-expanded={hasChildren ? (collapsed ? flyoutOpen : treeExpanded) : undefined}
      aria-haspopup={hasChildren && collapsed ? 'menu' : undefined}
      aria-controls={hasChildren && collapsed && flyoutOpen && flyoutId ? flyoutId : undefined}
      className={[
        styles.row,
        isActive || isGroupActive ? styles.rowActive : '',
        collapsed ? styles.rowCollapsed : '',
        depth > 0 && !collapsed ? styles.rowNested : '',
        flyoutOpen ? styles.rowFlyoutOpen : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
    >
      {isActive && !collapsed ? <span className={styles.activeRail} aria-hidden="true" /> : null}
      {!collapsed && hasChildren ? (
        <span
          className={`${styles.treeChevron} ${treeExpanded ? styles.treeChevronOpen : ''}`}
          aria-hidden="true"
        >
          <CaretRightOutlined />
        </span>
      ) : null}
      {item.icon ? <span className={styles.icon}>{item.icon}</span> : null}
      {!collapsed ? <span className={styles.label}>{item.label}</span> : null}
      {!collapsed && item.count != null && item.count > 0 ? (
        <span className={`${styles.count} ${item.tone === 'error' ? styles.countError : ''}`}>{item.count}</span>
      ) : null}
      {collapsed && item.count != null && item.count > 0 ? (
        <span
          className={`${styles.countDot} ${item.tone === 'error' ? styles.countDotError : ''}`}
          aria-hidden="true"
        />
      ) : null}
    </button>
  );
}

function CollapsedFlyoutMenu({
  item,
  activeKey,
  flyoutId,
  anchorRect,
  flyoutRef,
  onNavigate,
  onClose,
}: {
  item: SideNavItem;
  activeKey: string | null;
  flyoutId: string;
  anchorRect: DOMRect;
  flyoutRef: React.RefObject<HTMLDivElement>;
  onNavigate?: (key: string) => void;
  onClose: () => void;
}) {
  return createPortal(
    <div
      ref={flyoutRef}
      id={flyoutId}
      role="menu"
      aria-label={item.label}
      className={styles.flyout}
      style={{
        top: anchorRect.top,
        left: anchorRect.right + 8,
      }}
    >
      <p className={styles.flyoutTitle}>{item.label}</p>
      {item.children!.map((child) => (
        <NavRow
          key={child.key}
          item={child}
          depth={1}
          activeKey={activeKey}
          collapsed={false}
          onNavigate={(key) => {
            onNavigate?.(key);
            onClose();
          }}
        />
      ))}
    </div>,
    document.body
  );
}

function NavGroup({
  item,
  activeKey,
  collapsed,
  flyoutOpen,
  treeExpanded,
  onToggleFlyout,
  onToggleTree,
  onNavigate,
  flyoutId,
  onRegisterTrigger,
}: {
  item: SideNavItem;
  activeKey: string | null;
  collapsed: boolean;
  flyoutOpen: boolean;
  treeExpanded: boolean;
  onToggleFlyout: () => void;
  onToggleTree: () => void;
  onNavigate?: (key: string) => void;
  flyoutId: string;
  onRegisterTrigger: (key: string, node: HTMLButtonElement | null) => void;
}) {
  const hasChildren = Boolean(item.children && item.children.length > 0);

  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      if (collapsed && hasChildren) {
        onRegisterTrigger(item.key, node);
      }
    },
    [collapsed, hasChildren, item.key, onRegisterTrigger]
  );

  return (
    <div className={collapsed && hasChildren ? styles.collapsedGroup : undefined}>
      <NavRow
        item={item}
        depth={0}
        activeKey={activeKey}
        collapsed={collapsed}
        hasChildren={hasChildren}
        flyoutOpen={flyoutOpen}
        treeExpanded={treeExpanded}
        flyoutId={flyoutId}
        buttonRef={setTriggerRef}
        onNavigate={onNavigate}
        onToggleFlyout={onToggleFlyout}
        onToggleTree={onToggleTree}
      />
      {!collapsed && hasChildren && treeExpanded ? (
        <div className={styles.childList}>
          {item.children!.map((child) => (
            <NavRow
              key={child.key}
              item={child}
              depth={1}
              activeKey={activeKey}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export interface SideNavProps {
  items?: SideNavItem[];
  activeKey?: string | null;
  collapsed?: boolean;
  mobileOpen?: boolean;
  onToggleCollapse?: () => void;
  onToggleHidden?: () => void;
  onNavigate?: (key: string) => void;
  footer?: React.ReactNode;
  className?: string;
}

export const SideNav: React.FC<SideNavProps> = ({
  items = [],
  activeKey = null,
  collapsed = false,
  mobileOpen = true,
  onToggleCollapse,
  onToggleHidden,
  onNavigate,
  footer = null,
  className,
}) => {
  const [openFlyoutKey, setOpenFlyoutKey] = useState<string | null>(null);
  const [flyoutAnchorRect, setFlyoutAnchorRect] = useState<DOMRect | null>(null);
  /** Expanded tree groups when the sidebar labels are visible (not icon-rail). */
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Record<string, boolean>>({});
  const navRef = useRef<HTMLElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const triggerRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const flyoutIdPrefix = useId();

  const registerTrigger = useCallback((key: string, node: HTMLButtonElement | null) => {
    if (node) {
      triggerRefs.current.set(key, node);
      return;
    }
    triggerRefs.current.delete(key);
  }, []);

  const closeFlyout = useCallback(() => {
    setOpenFlyoutKey(null);
    setFlyoutAnchorRect(null);
  }, []);

  // Keep the group open when a child route is active (file-explorer style).
  useEffect(() => {
    if (!activeKey) return;
    setExpandedGroupKeys((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const item of items) {
        if (item.children?.length && itemOrChildActive(item, activeKey) && !next[item.key]) {
          next[item.key] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [activeKey, items]);

  const updateFlyoutPosition = useCallback(() => {
    if (!openFlyoutKey) {
      setFlyoutAnchorRect(null);
      return;
    }
    const trigger = triggerRefs.current.get(openFlyoutKey);
    if (!trigger) {
      setFlyoutAnchorRect(null);
      return;
    }
    setFlyoutAnchorRect(trigger.getBoundingClientRect());
  }, [openFlyoutKey]);

  useLayoutEffect(() => {
    updateFlyoutPosition();
  }, [updateFlyoutPosition, collapsed, items]);

  useEffect(() => {
    if (!openFlyoutKey) return undefined;

    const handleReposition = () => updateFlyoutPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    navRef.current?.addEventListener('scroll', handleReposition);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
      navRef.current?.removeEventListener('scroll', handleReposition);
    };
  }, [openFlyoutKey, updateFlyoutPosition]);

  useEffect(() => {
    if (!collapsed) {
      closeFlyout();
    }
  }, [collapsed, closeFlyout]);

  useEffect(() => {
    if (!openFlyoutKey) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (navRef.current?.contains(target)) return;
      if (flyoutRef.current?.contains(target)) return;
      closeFlyout();
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [openFlyoutKey, closeFlyout]);

  const openFlyoutItem = openFlyoutKey
    ? items.find((item) => item.key === openFlyoutKey && item.children && item.children.length > 0)
    : undefined;

  return (
    <aside
      className={[
        styles.sideNav,
        collapsed ? styles.sideNavCollapsed : '',
        !mobileOpen ? styles.sideNavMobileHidden : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="app-side-nav"
      aria-label="Application navigation"
    >
      <nav className={styles.navScroll} ref={navRef}>
        {items.map((item) => (
          <NavGroup
            key={item.key}
            item={item}
            activeKey={activeKey}
            collapsed={collapsed}
            flyoutOpen={openFlyoutKey === item.key}
            treeExpanded={Boolean(expandedGroupKeys[item.key])}
            flyoutId={`${flyoutIdPrefix}-${item.key}`}
            onRegisterTrigger={registerTrigger}
            onToggleFlyout={() =>
              setOpenFlyoutKey((current) => (current === item.key ? null : item.key))
            }
            onToggleTree={() =>
              setExpandedGroupKeys((prev) => ({
                ...prev,
                [item.key]: !prev[item.key],
              }))
            }
            onNavigate={onNavigate}
          />
        ))}
      </nav>
      <div className={styles.footer}>
        {footer}
        {onToggleCollapse ? (
          <button
            type="button"
            className={`${styles.collapseBtn} ${collapsed ? styles.collapseBtnCollapsed : ''}`}
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand navigation' : 'Collapse to icons'}
          >
            <span className={`${styles.chevron} ${collapsed ? styles.chevronCollapsed : ''}`}>
              <LeftOutlined />
            </span>
            {!collapsed ? 'Collapse' : null}
          </button>
        ) : null}
        {/* Hide-sidebar control kept in SideNav but not wired from AppShell (Orlin may prefer it later). */}
        {onToggleHidden ? (
          <button
            type="button"
            className={`${styles.collapseBtn} ${collapsed ? styles.collapseBtnCollapsed : ''}`}
            onClick={onToggleHidden}
            title="Hide sidebar"
          >
            <MenuFoldOutlined />
            {!collapsed ? 'Hide sidebar' : null}
          </button>
        ) : null}
      </div>
      {collapsed && openFlyoutItem && flyoutAnchorRect ? (
        <CollapsedFlyoutMenu
          item={openFlyoutItem}
          activeKey={activeKey}
          flyoutId={`${flyoutIdPrefix}-${openFlyoutItem.key}`}
          anchorRect={flyoutAnchorRect}
          flyoutRef={flyoutRef}
          onNavigate={onNavigate}
          onClose={closeFlyout}
        />
      ) : null}
    </aside>
  );
};
